import { Empty, Flex } from "antd";
import { useMemo } from "react";
import { shallowEqual } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Schedule, WorkStatus } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import { ROUTES } from "@/constants";
import { useAddUnscheduledVisits } from "@/domains/point";
import { ScheduleCard, ScheduleMenuConfig } from "@/domains/route";
import { useAppSelector } from "@/hooks/redux";

import { MyRouteCapabilities } from "../MyRoute.policy";
import { MyRouteData } from "../MyRoute.types";

interface RouteScheduleListBlockProps {
    capabilities: MyRouteCapabilities;
    points: MyRouteData["points"];
    schedules: MyRouteData["schedules"];
}

export const RouteScheduleListBlock = ({
    capabilities,
    points,
    schedules,
}: RouteScheduleListBlockProps) => {
    const { pointIds, pointExtraIds, workStatus } = useAppSelector(
        (s) => ({
            pointIds: s.work.pointIds,
            pointExtraIds: s.work.pointExtraIds,
            workStatus: s.work.workStatus,
        }),
        shallowEqual,
    );
    const isUnscheduledPointsProcessing = useAppSelector(
        (s) => s.offline.isUnscheduledPointsProcessing,
    );

    const navigate = useNavigate();
    const { addUnscheduledVisits } = useAddUnscheduledVisits();

    const baseMenu = useMemo<ScheduleMenuConfig[]>(
        () => [
            {
                type: "view",
                label: "Переглянути ТТ",
                onClick: (item: Schedule) => {
                    void navigate(
                        buildRoutePath(ROUTES.PointDetails, {
                            pointId: item.pointId,
                        }),
                    );
                },
            },
        ],
        [navigate],
    );

    const unplannedMenuItem = useMemo<ScheduleMenuConfig>(
        () => ({
            type: "addUnplanned",
            label: "Додати на сьогодні",
            onClick: (item: Schedule) => {
                addUnscheduledVisits({ pointIds: [item.pointId] });
            },
            disabled: () => isUnscheduledPointsProcessing,
        }),
        [addUnscheduledVisits, isUnscheduledPointsProcessing],
    );

    const pointIdsSet = useMemo(
        () => new Set([...pointIds, ...pointExtraIds]),
        [pointExtraIds, pointIds],
    );

    return (
        <Flex vertical gap={8}>
            {schedules.length ? (
                schedules.map((schedule) => {
                    const currentPoint = points[schedule.pointId];
                    const canAddUnplanned =
                        !pointIdsSet.has(schedule.pointId) &&
                        workStatus === WorkStatus.StartWork;

                    const menu = canAddUnplanned
                        ? [...baseMenu, unplannedMenuItem]
                        : baseMenu;

                    if (!currentPoint) return null;

                    return (
                        <ScheduleCard
                            key={schedule.pointId}
                            schedule={schedule}
                            point={
                                capabilities.canSeeDebtMarker
                                    ? currentPoint
                                    : {
                                          ...currentPoint,
                                          hasDebt: false,
                                      }
                            }
                            scheduleMenuConfig={menu}
                            disableDrag
                        />
                    );
                })
            ) : (
                <Empty description="Нічого не знайдено" />
            )}
        </Flex>
    );
};
